// src/pages/Login.jsx
import '../styles/login.css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { 
  Box, 
  Text, 
  Input, 
  Button, 
  Flex, 
  VStack, 
  InputGroup, 
  InputLeftElement,
  IconButton,
  InputRightElement,
  Link,
  useToast
} from '@chakra-ui/react';
import { 
  MdMail, 
  MdLock, 
  MdArrowBack, 
  MdVisibility, 
  MdVisibilityOff 
} from 'react-icons/md';
import { login, conversationStarted } from '../api/auth';
import { useAuth } from '../context/AuthContext';

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  Username: Yup.string()
    .required('Username is required'),
  Password: Yup.string()
    .required('Password is required')
});

export default function Login() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (values, { setSubmitting }) => {
    try {
      const { driver_id, session_id } = await login(values.Username, values.Password);
      setToken(localStorage.getItem('token'));
      // Initialize the driver in memory
      await conversationStarted(driver_id);
      
      toast({
        title: 'Login Successful',
        description: 'Welcome to the app!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/search');
    } catch (err) {
      toast({
        title: 'Login Failed',
        description: err.response?.data?.detail || 'Invalid username or password!',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate('/role-select');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box 
      minH="100vh" 
      bgGradient="linear(to-b, #FFF3E3, #E8AA7A)"
      bgSize="cover"
      position="relative"
    >
      {/* Logistics Truck Map Animation would be here */}
      <Box 
        height="60vh" 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        position="relative"
      >
        {/* Placeholder for truck animation - you would replace this with actual component */}
        <Box 
          width="80px" 
          height="80px" 
          borderRadius="full" 
          bg="#FD984F" 
          display="flex" 
          justifyContent="center" 
          alignItems="center"
        >
          <Box as="span" fontSize="2xl" color="white">🚚</Box>
        </Box>
      </Box>

      {/* Back Button */}
      <IconButton
        icon={<MdArrowBack />}
        position="absolute"
        top="30px"
        left="20px"
        zIndex={1}
        borderRadius="full"
        bg="white"
        size="lg"
        onClick={handleGoBack}
      />

      {/* Content Area */}
      <Box 
        w="100%" 
        position="absolute" 
        bottom="0"
        borderTopLeftRadius="42px"
        borderTopRightRadius="42px"
        bg="white"
        boxShadow="0px -4px 10px rgba(0, 0, 0, 0.1)"
      >
        {/* Title Container */}
        <Box px="24px" pt="30px">
          <Text
            fontSize="34px"
            fontWeight="600"
            color="#202020"
            lineHeight="1.2"
            letterSpacing="1px"
            mb="8px"
            fontFamily="'Poppins', sans-serif"
          >
            Welcome to KAYAAN!
          </Text>
          <Text
            color="#2E2E2E"
            fontSize="16px"
            fontWeight="400"
            lineHeight="26px"
            letterSpacing="0.88px"
            mb="20px"
            fontFamily="'Poppins', sans-serif"
          >
            Let's personalize your experience.
          </Text>
        </Box>

        {/* Form Area */}
        <Box px="24px" mb="16px">
          <Formik
            initialValues={{ 
              Username: 'test_user', // Autofill test username
              Password: 'test123'  // Autofill test password
            }}
            validationSchema={validationSchema}
            onSubmit={handleLogin}
          >
            {({
              handleChange,
              handleBlur,
              handleSubmit,
              values,
              errors,
              touched,
              isSubmitting
            }) => (
              <VStack spacing={4} align="stretch">
                {/* Username Input */}
                <Box mb="15px">
                  <Text 
                    fontSize="20px" 
                    fontWeight="500" 
                    color="#333"
                    mb="8px"
                    pl="10px"
                    letterSpacing="1px"
                    lineHeight="24px"
                    fontFamily="'Poppins', sans-serif"
                  >
                    Username
                  </Text>
                  <InputGroup
                    h="72px"
                    borderWidth="2px"
                    borderColor="#F2F3F3"
                    borderRadius="42px"
                    bg="#F2F3F3"
                    pl="20px"
                  >
                    <InputLeftElement pointerEvents="none" h="100%" pl="10px">
                      <MdMail color="#888" size={22} />
                    </InputLeftElement>
                    <Input
                      type="text"
                      placeholder="Enter username"
                      value={values.Username}
                      onChange={handleChange('Username')}
                      onBlur={handleBlur('Username')}
                      border="none"
                      h="100%"
                      fontSize="18px"
                      fontWeight="500"
                      letterSpacing="0.9px"
                      _focus={{ boxShadow: 'none' }}
                      pl="40px"
                      fontFamily="'Poppins', sans-serif"
                    />
                  </InputGroup>
                  {touched.Username && errors.Username && (
                    <Text color="red" fontSize="14px" mt="8px" pl="20px">
                      {errors.Username}
                    </Text>
                  )}
                </Box>

                {/* Password Input */}
                <Box mb="15px">
                  <Text 
                    fontSize="20px" 
                    fontWeight="500" 
                    color="#333"
                    mb="8px"
                    pl="10px"
                    letterSpacing="1px"
                    lineHeight="24px"
                    fontFamily="'Poppins', sans-serif"
                  >
                    Password
                  </Text>
                  <InputGroup
                    h="72px"
                    borderWidth="2px"
                    borderColor="#F2F3F3"
                    borderRadius="42px"
                    bg="#F2F3F3"
                    pl="20px"
                  >
                    <InputLeftElement pointerEvents="none" h="100%" pl="10px">
                      <MdLock color="#888" size={22} />
                    </InputLeftElement>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter Password"
                      value={values.Password}
                      onChange={handleChange('Password')}
                      onBlur={handleBlur('Password')}
                      border="none"
                      h="100%"
                      fontSize="18px"
                      fontWeight="500"
                      letterSpacing="0.9px"
                      _focus={{ boxShadow: 'none' }}
                      pl="40px"
                      fontFamily="'Poppins', sans-serif"
                    />
                    <InputRightElement h="100%" width="4.5rem" pr="10px">
                      <IconButton
                        h="40px"
                        size="sm"
                        bg="transparent"
                        _hover={{ bg: 'transparent' }}
                        icon={showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                        onClick={togglePasswordVisibility}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {touched.Password && errors.Password && (
                    <Text color="red" fontSize="14px" mt="8px" pl="20px">
                      {errors.Password}
                    </Text>
                  )}
                </Box>

                {/* Forgot Password */}
                <Text
                  textAlign="right"
                  color="#383838"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  letterSpacing="0.8px"
                  mb="28px"
                  mt="-5px"
                  fontFamily="'Poppins', sans-serif"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Forgot password ?
                </Text>

                {/* Login Button */}
                <Button
                  h="72px"
                  borderRadius="42px"
                  bg="#FD984F"
                  color="#202020"
                  fontSize="20px"
                  fontWeight="500"
                  lineHeight="24px"
                  letterSpacing="1px"
                  mb="16px"
                  _hover={{ bg: '#FFE8CC' }}
                  _active={{ bg: '#FFD6AA' }}
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  fontFamily="'Poppins', sans-serif"
                >
                  Login
                </Button>
              </VStack>
            )}
          </Formik>
        </Box>

        {/* Create Account Footer */}
        <Box
          w="100%"
          borderTopRadius="24px"
          bg="#FFF2E5"
          p="18px"
          mt="auto"
        >
          <Text
            color="rgba(0, 0, 0, 0.90)"
            fontSize="16px"
            fontWeight="400"
            letterSpacing="0.8px"
            textAlign="center"
            fontFamily="'Poppins', sans-serif"
          >
            New User ?{" "}
            <Link
              color="#FD984F"
              fontWeight="500"
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
              onClick={() => navigate("/signup")}
            >
              Create Account
            </Link>
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
